import React from 'react';
import { FieldError, Merge } from 'react-hook-form';

import { InputLabel } from '@mui/material';
import Autocomplete, {
  createFilterOptions,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  AutocompleteInputChangeReason,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { SxProps, Theme } from '@mui/material/styles';

import { Asterisk } from '@/components/Asterisk';

type AutocompleteSelectProps<T> = {
  id?: string;
  options: T[];
  value?: T | T[];
  limitTags?: number;
  readOnly?: boolean;
  disabled?: boolean;
  inputValue?: string;
  multiple?: boolean;
  sx?: SxProps<Theme>;
  isRequired?: boolean;
  labelSx?: SxProps<Theme>;
  filterSelectedOptions?: boolean;
  error?: Merge<FieldError, FieldError[]>;
  label?: React.ReactNode | string | null;
  getOptionLabel?: (option: T) => string;
  onChange?: (
    event: React.SyntheticEvent,
    value: T | T[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>,
  ) => void;
  onInputChange?: (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => void;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState,
  ) => React.ReactNode;
  tagsLabel?: (more: number) => React.ReactNode;
  groupBy?: (option: T) => string;
  getOptionDisabled?: (option: T) => boolean;
};

export const AutoCompleteMultipleSelect = <T extends { label: string; value: string }>({
  id,
  sx,
  error,
  value,
  label,
  options,
  labelSx,
  multiple,
  disabled,
  limitTags,
  inputValue,
  isRequired,
  filterSelectedOptions,
  getOptionDisabled,
  getOptionLabel,
  onInputChange,
  renderOption,
  tagsLabel,
  groupBy,
  onChange,
}: AutocompleteSelectProps<T>) => {
  const filterOptions = createFilterOptions({
    stringify: (option: T) => option.label,
    ignoreCase: true,
    ignoreAccents: true,
  });

  const getOptionKey = (option: T) => option.value;
  return (
    <>
      <InputLabel htmlFor={id} sx={labelSx}>
        {label}
        {isRequired ? <Asterisk /> : ''}
      </InputLabel>
      <Autocomplete
        sx={sx}
        id={id}
        value={value}
        disableListWrap
        disableClearable
        options={options}
        disabled={disabled}
        multiple={multiple}
        disableCloseOnSelect
        limitTags={limitTags}
        inputValue={inputValue}
        filterSelectedOptions={filterSelectedOptions}
        renderInput={(params) => (
          <TextField
            sx={{ backgroundColor: 'white' }}
            {...params}
            error={!!error}
            helperText={error?.message}
            variant='outlined'
          />
        )}
        isOptionEqualToValue={(option: T, currValue: T) =>
          option?.value === currValue?.value && option?.label === currValue?.label
        }
        getOptionDisabled={getOptionDisabled}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        onInputChange={onInputChange}
        getLimitTagsText={tagsLabel}
        getOptionKey={getOptionKey}
        renderOption={renderOption}
        onChange={onChange}
        groupBy={groupBy}
      />
    </>
  );
};
